import React, { useState, useEffect, useCallback } from 'react';
import { AuthContext } from './context/AuthContext';
import { api } from './utils/api';
import { registerPushSubscription, isPushSupported, isPushSubscribed } from './utils/pushNotifications';

// Import components
import LoginPage from './components/auth/LoginPage';
import WelcomePage from './components/auth/WelcomePage';
import Dashboard from './components/admin/Dashboard';
import DriverApp from './components/driver/DriverApp';
import GroupViewDashboard from './components/group-view/GroupViewDashboard';
import SystemAdminDashboard from './components/sysadmin/SystemAdminDashboard';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isWelcomePage, setIsWelcomePage] = useState(false);

  const loadUser = useCallback(() => {
    return api('/auth/me')
      .then(u => { setUser(u); return u; })
      .catch((err) => {
        console.warn('[LAWD App] loadUser failed:', err.message);
        localStorage.removeItem('lawd_expires_at');
        localStorage.removeItem('lawd_user');
        return null;
      });
  }, []);

  // Load user on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const isWelcome = window.location.pathname === '/welcome' || token;

    if (isWelcome && token) {
      setIsWelcomePage(true);
      setLoading(false);
      return;
    }

    const hasSession = localStorage.getItem('lawd_expires_at');
    if (!hasSession) {
      setLoading(false);
      return;
    }

    // api() handles 401 → refresh → retry internally
    loadUser().finally(() => setLoading(false));
  }, [loadUser]);

  // Re-validate session when user returns to a backgrounded tab
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (!user) return;

      // Only call /auth/me if the token is expired/near expiry (refresh needed)
      // Otherwise use the stored user — no network call needed
      const expiresAt = localStorage.getItem('lawd_expires_at');
      const timeLeft = expiresAt ? (Number(expiresAt) * 1000 - Date.now()) : 0;

      if (timeLeft > 300000) {
        // Token is still fresh (>5min left) — no need to re-validate
        return;
      }

      // Token expired or expiring soon — try to refresh silently
      // Do NOT force-logout on failure — could be temporary server restart
      api('/auth/me')
        .then(freshUser => setUser(freshUser))
        .catch(() => {
          console.warn('[LAWD App] Session check failed — will retry on next interaction');
          // Do NOT clear tokens here. The token/refresh might still be valid.
          // Let the next user action trigger a proper refresh via api() 401 handler.
        });
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user]);

  // Cross-tab sync: if another tab logs out (clears session), this tab follows
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'lawd_expires_at') {
        if (!e.newValue && user) {
          console.log('[LAWD App] Session cleared by another tab — logging out');
          setUser(null);
        } else if (e.newValue && !user) {
          loadUser();
        }
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [user, loadUser]);

  // Auto-register push subscription when user logs in
  useEffect(() => {
    if (!user) return;
    if (!isPushSupported()) return;
    isPushSubscribed().then(subscribed => {
      if (!subscribed) {
        registerPushSubscription().catch(() => {});
      }
    });
  }, [user]);

  const handleLogout = () => {
    // Call logout endpoint to clear httpOnly cookies
    fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    }).catch(() => {});
    localStorage.removeItem('lawd_expires_at');
    localStorage.removeItem('lawd_user');
    setUser(null);
  };

  const handleWelcomeLogin = (userData) => {
    setIsWelcomePage(false);
    setUser(userData);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Show welcome page for invite links
  if (isWelcomePage && !user) {
    return <WelcomePage onLogin={handleWelcomeLogin} />;
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {user ? (
        (user.role === 'DRIVER' || user.role === 'OWNER_DRIVER') ? (
          <DriverApp user={user} onLogout={handleLogout} />
        ) : user.role === 'GROUP_VIEWER' ? (
          <GroupViewDashboard user={user} onLogout={handleLogout} />
        ) : user.role === 'SYSTEM_ADMIN' ? (
          <SystemAdminDashboard user={user} onLogout={handleLogout} />
        ) : (
          <Dashboard user={user} onLogout={handleLogout} />
        )
      ) : (
        <LoginPage onLogin={setUser} />
      )}
    </AuthContext.Provider>
  );
}

export default App;
